// src/pages/EditPoem.js
import { useEffect, useState } from "react";
import { getApiUrl } from "../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../context/ToastContext";
import Modal from "../components/Modal";
import CoverImageCropModal from "../components/CoverImageCropModal";
import UploadProgressBar from "../components/UploadProgressBar";
import {
  getAvatarUrl,
  getPoemImageUrl,
  DEFAULT_AVATAR_DATA_URL,
  FALLBACK_AVATAR_SVG,
} from "../utils/avatar";
import { isAllowedImageType, readFileAsDataUrl } from "../utils/imageCrop";
import { uploadFormData } from "../utils/uploadWithProgress";
import {
  Editor,
  EditorState,
  convertToRaw,
  convertFromRaw,
  RichUtils,
  SelectionState,
  ContentState,
  Modifier,
} from "draft-js";
import "draft-js/dist/Draft.css";
import {
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaPen,
  FaPenNib,
  FaImage,
  FaTimes,
} from "react-icons/fa";

export default function EditPoem() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [poem, setPoem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wasDraft, setWasDraft] = useState(false);
  const [title, setTitle] = useState("");
  const [alignment, setAlignment] = useState("left");
  const [tags, setTags] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [tagInputFocused, setTagInputFocused] = useState(false);
  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty()
  );
  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    type: "info",
  });
  const [editorFocused, setEditorFocused] = useState(false);
  const [collabModal, setCollabModal] = useState(false);
  const [inviteUsername, setInviteUsername] = useState("");
  const [collabLists, setCollabLists] = useState({ collaborators: [], invites: [] });
  const [collabSuggestions, setCollabSuggestions] = useState([]);
  const [showCollabSuggestions, setShowCollabSuggestions] = useState(false);
  const [collabDebounce, setCollabDebounce] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [removeCoverImage, setRemoveCoverImage] = useState(false);
  const [copyrightEnabled, setCopyrightEnabled] = useState(true);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    return () => {
      if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
    };
  }, [coverImagePreview]);

  const handleCoverImageChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!isAllowedImageType(file)) {
      setModal({
        open: true,
        title: "Invalid File",
        message:
          "Please choose a JPEG, PNG, GIF, or WebP image for your poem.",
        type: "error",
      });
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCropImageSrc(dataUrl);
      setIsCropOpen(true);
    } catch {
      setModal({
        open: true,
        title: "Image Error",
        message: "Could not load that image. Please try another file.",
        type: "error",
      });
    }
  };

  const handleCoverCropComplete = (file, previewUrl) => {
    if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
    setCoverImageFile(file);
    setCoverImagePreview(previewUrl);
    setRemoveCoverImage(false);
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleCoverCropCancel = () => {
    setIsCropOpen(false);
    setCropImageSrc(null);
  };

  const handleRemoveCoverImage = () => {
    if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
    setCoverImageFile(null);
    setCoverImagePreview(null);
    setRemoveCoverImage(true);
  };

  // Helper function to strip alignment markers
  const stripAlignmentMarker = (text) => {
    if (typeof text !== "string") return text;
    try {
      const m = text.match(
        /^\s*\[\[ALIGN:(left|center|right|justify)\]\]\s*\n?/i
      );
      if (m) return text.replace(m[0], "");
      return text;
    } catch (_) {
      return text;
    }
  };

  useEffect(() => {
    const fetchPoem = async () => {
      try {
        // reset any prior modal state
        setModal((m) => ({ ...m, open: false, title: "", message: "" }));
        const token = localStorage.getItem("token");
        const res = await fetch(getApiUrl(`/api/poems/${id}`), {
          credentials: "include",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const raw = await res.json().catch(() => ({}));
        if (!res.ok) {
          if (res.status === 404) {
            setModal({
              open: true,
              title: "Not Found",
              message:
                raw?.msg ||
                "This draft is not accessible. Make sure you're logged in as the author.",
              type: "error",
            });
          } else {
            setModal({
              open: true,
              title: "Error",
              message: raw?.error || raw?.msg || "Failed to load poem.",
              type: "error",
            });
          }
          setLoading(false);
          return;
        }

        const data = raw?.poem ? raw.poem : raw; // normalize {poem} or direct object
        setPoem(data);
        setWasDraft(!!data?.isDraft);
        setTitle(data.title || "");
        setAlignment(data.alignment || "left");
        setTags(Array.isArray(data.tags) ? data.tags.join(", ") : "");
        setCopyrightEnabled(data?.copyrightEnabled ?? true);
        // Try to parse content as Draft.js raw, fallback to plain text
        let contentState;
        try {
          const rawContent =
            typeof data.content === "string"
              ? JSON.parse(data.content)
              : data.content;

          // Check if first block has alignment marker and clean it
          if (rawContent && rawContent.blocks && rawContent.blocks.length > 0) {
            const firstBlock = rawContent.blocks[0];
            if (firstBlock.text) {
              const cleanText = stripAlignmentMarker(firstBlock.text);
              if (cleanText !== firstBlock.text) {
                rawContent.blocks[0] = { ...firstBlock, text: cleanText };
              }
            }
          }

          contentState = convertFromRaw(rawContent);
        } catch {
          // fallback for plain text - strip alignment markers
          const cleanContent = stripAlignmentMarker(data.content || "");
          contentState = ContentState.createFromText(cleanContent);
        }
        setEditorState(
          contentState
            ? EditorState.createWithContent(contentState)
            : EditorState.createEmpty()
        );
        setLoading(false);
      } catch (err) {
        setModal({
          open: true,
          title: "Error",
          message: "Failed to load poem. Please try again.",
          type: "error",
        });
        setLoading(false);
      }
    };

    fetchPoem();
    // load collaborators
    (async () => {
      try {
        const r = await fetch(getApiUrl(`/api/poems/${id}/collaborators`));
        if (r.ok) setCollabLists(await r.json());
      } catch {}
    })();
  }, [id]);

  // Toolbar handlers
  const handleKeyCommand = (command, editorState) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return "handled";
    }
    return "not-handled";
  };

  const onFormatClick = (style) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };

  // Remove per-block editor alignment helper: using global poem alignment

  function getAlignmentState(align) {
    if (!editorFocused) return "inactive";
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const startKey = selection.getStartKey();
    const endKey = selection.getEndKey();

    let key = startKey;
    let hasAlign = false;
    let noAlign = false;
    while (key) {
      const block = content.getBlockForKey(key);
      const blockAlign = block.getData().get("textAlign");
      if (blockAlign === align) {
        hasAlign = true;
      } else {
        noAlign = true;
      }
      if (key === endKey) break;
      key = content.getKeyAfter(key);
    }
    if (hasAlign && noAlign) return "mixed";
    if (hasAlign) return "active";
    return "inactive";
  }

  // Helper to check if a style is active or mixed
  function getInlineStyleState(style) {
    if (!editorFocused) return "inactive";
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    if (selection.isCollapsed()) {
      return editorState.getCurrentInlineStyle().has(style)
        ? "active"
        : "inactive";
    }
    const start = selection.getStartOffset();
    const end = selection.getEndOffset();
    const block = content.getBlockForKey(selection.getStartKey());
    let hasStyle = false;
    let noStyle = false;
    for (let i = start; i < end; i++) {
      if (block.getInlineStyleAt(i).has(style)) {
        hasStyle = true;
      } else {
        noStyle = true;
      }
    }
    if (hasStyle && noStyle) return "mixed";
    if (hasStyle) return "active";
    return "inactive";
  }

  const updatePoem = async (e, isDraftFlag = null) => {
    e.preventDefault();

    // Check title length and show toast if it exceeds 200 characters (for all updates except saving as draft)
    if (isDraftFlag !== true) {
      if (!title || title.trim().length === 0) {
        toast.error("Please enter a title for your poem.", {
          title: "Title Required",
        });
        return;
      }
      
      if (title.length > 200) {
        toast.warning(`Title is ${title.length} characters. Maximum allowed is 200 characters. Please shorten it before updating.`, {
          title: "Title Too Long",
        });
        return;
      }
    }

    // Convert Draft.js content to raw JSON string
    const current = editorState.getCurrentContent();
    const plainText = current.getPlainText().trim();
    
    // Validate that poem has actual content when publishing (not for drafts)
    if (isDraftFlag === false) {
      // Publishing - content is required
      if (!plainText || plainText.length === 0) {
        setModal({
          open: true,
          title: "Content Required",
          message: "Please enter some content for your poem before publishing.",
          type: "error",
        });
        return;
      }
    }

    // Check tag limit before processing
    if (tags) {
      const currentTags = tags
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);

      if (currentTags.length > 5) {
        setModal({
          open: true,
          title: "Too Many Tags",
          message:
            "You can add a maximum of 5 tags to your poem. Please remove some tags and try again.",
          type: "error",
        });
        return;
      }
    }

    // Convert Draft.js content to raw JSON string
    const alignedBlockMap = current
      .getBlockMap()
      .map((blk) => blk.set("data", blk.getData().set("textAlign", alignment)));
    const alignedContent = current.merge({ blockMap: alignedBlockMap });
    const contentRaw = JSON.stringify(convertToRaw(alignedContent));
    const form = new FormData();
    form.append("title", title);
    form.append("content", contentRaw);
    form.append("alignment", alignment);
    form.append("font", "serif");
    form.append("tags", tags || "");
    form.append("copyrightEnabled", String(!!copyrightEnabled));
    form.append(
      "copyrightHolder",
      String(poem?.author?.username || "")
        .trim()
        .slice(0, 140)
    );
    form.append(
      "copyrightYear",
      String(
        poem?.copyrightYear ||
          new Date().getFullYear()
      )
    );
    if (isDraftFlag !== null) form.append("isDraft", String(!!isDraftFlag));
    if (coverImageFile) form.append("coverImage", coverImageFile);
    if (removeCoverImage) form.append("removeCoverImage", "true");

    setIsUploading(true);
    setUploadProgress(0);
    let ok = false;
    let updated = null;
    try {
      const result = await uploadFormData({
        url: getApiUrl(`/api/poems/${id}`),
        method: "PUT",
        body: form,
        onProgress: setUploadProgress,
      });
      ok = result.ok;
      updated = result.data;
      setUploadProgress(100);
    } catch {
      setModal({
        open: true,
        title: "Error",
        message: "Update failed. Please check your connection and try again.",
        type: "error",
      });
      return;
    } finally {
      setIsUploading(false);
    }

    if (ok) {
      setModal({
        open: true,
        title: "Success",
        message: isDraftFlag === true ? "Draft updated!" : "Poem updated!",
        type: "success",
      });
      setTimeout(() => {
        if (isDraftFlag === true) {
          navigate("/profile");
        } else {
          navigate(`/poems/${id}`);
        }
      }, 800);
    } else {
      setModal({
        open: true,
        title: "Error",
        message: updated.error || "Update failed",
        type: "error",
      });
    }
  };

  // Fetch tag suggestions from trending tags
  const fetchTagSuggestions = async (query) => {
    try {
      const res = await fetch(
        getApiUrl(`/api/poems/tags/trending?limit=20`)
      );
      if (res.ok) {
        const tags = await res.json();
        const filtered = tags
          .filter((tagData) =>
            tagData.tag.toLowerCase().includes(query.toLowerCase())
          )
          .slice(0, 5);
        setTagSuggestions(filtered);
        setShowTagSuggestions(filtered.length > 0);
      }
    } catch (err) {
      console.error("Error fetching tag suggestions:", err);
    }
  };

  // Handle tag suggestion click
  const handleTagSuggestionClick = (tag) => {
    const currentTags = tags ? tags.split(",").map((t) => t.trim()) : [];

    // Check if we already have 5 tags
    if (currentTags.length >= 5) {
      setShowTagSuggestions(false);
      return;
    }

    if (!currentTags.includes(tag)) {
      // Replace the last incomplete tag with the selected tag
      const tagsArray = tags ? tags.split(",") : [];
      if (tagsArray.length > 0) {
        // Remove the last incomplete tag and add the selected one
        tagsArray[tagsArray.length - 1] = tag;
        const newTags = tagsArray.join(", ");
        setTags(newTags);
      } else {
        // If no tags yet, just add the selected tag
        setTags(tag);
      }
    }
    setShowTagSuggestions(false);
  };

  // Handle tags input change
  const handleTagsChange = (e) => {
    const value = e.target.value;
    setTags(value);

    if (value.trim()) {
      // Split by comma and get all parts
      const allParts = value.split(",").map((t) => t.trim());
      const lastPart = allParts[allParts.length - 1];

      // Count only complete tags (exclude the last part if it's still being typed)
      const completeTags = allParts.slice(0, -1).filter((t) => t.length > 0);

      // Only show suggestions if we have less than 5 complete tags
      if (completeTags.length < 5) {
        if (lastPart && lastPart.length > 0) {
          fetchTagSuggestions(lastPart);
        } else {
          setTagSuggestions([]);
          setShowTagSuggestions(false);
        }
      } else {
        setTagSuggestions([]);
        setShowTagSuggestions(false);
      }
    } else {
      setTagSuggestions([]);
      setShowTagSuggestions(false);
    }
  };

  const handleUpdate = (e) => updatePoem(e, null);
  const handlePublish = (e) => updatePoem(e, false);
  const handleSaveDraft = (e) => updatePoem(e, true);

  if (loading) return <p>Loading...</p>;
  if (!poem) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Modal
          open={modal.open}
          title={modal.title}
          message={modal.message}
          type={modal.type}
          onClose={() => setModal({ ...modal, open: false })}
        />
        <div className="peer-home-card p-6 text-center">
          <h2 className="text-lg font-semibold mb-2">Poem not available</h2>
          <p className="text-gray-600 mb-4">
            This draft may not be accessible. Make sure you are logged in as the
            author.
          </p>
          <button
            className="bg-gray-800 text-white px-4 py-2 rounded"
            onClick={() => navigate(-1)}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="poem-compose-page min-h-screen w-full max-w-full box-border overflow-x-hidden p-0 sm:p-6">
    <div className="poem-compose peer-home-page-card w-full max-w-none sm:max-w-2xl sm:mx-auto p-4 sm:p-6 min-w-0 box-border">
      <Modal
        open={modal.open}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        onClose={() => setModal({ ...modal, open: false })}
      />
      <h2
        className="text-2xl sm:text-3xl font-bold mb-2"
        style={{ fontFamily: "var(--font-peer-serif)", letterSpacing: "-0.02em" }}
      >
        Edit Poem
      </h2>
      <p className="text-sm text-[#777777] mb-4">
        Refine your lines, then save when you&apos;re ready.
      </p>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Align</span>
          <div className="inline-flex items-center gap-1">
            <button
              type="button"
              onClick={() => setAlignment("left")}
              className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                getAlignmentState("left") === "active"
                  ? "bg-[#f26522] text-white border-[#f26522]"
                  : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
              }`}
              title="Align left"
              aria-label="Align left"
            >
              <FaAlignLeft size={16} />
            </button>
            <button
              type="button"
              onClick={() => setAlignment("center")}
              className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                getAlignmentState("center") === "active"
                  ? "bg-[#f26522] text-white border-[#f26522]"
                  : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
              }`}
              title="Align center"
              aria-label="Align center"
            >
              <FaAlignCenter size={16} />
            </button>
            <button
              type="button"
              onClick={() => setAlignment("right")}
              className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                getAlignmentState("right") === "active"
                  ? "bg-[#f26522] text-white border-[#f26522]"
                  : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
              }`}
              title="Align right"
              aria-label="Align right"
            >
              <FaAlignRight size={16} />
            </button>
            <button
              type="button"
              onClick={() => setAlignment("justify")}
              className={`w-9 h-9 flex items-center justify-center border rounded-md ${
                getAlignmentState("justify") === "active"
                  ? "bg-[#f26522] text-white border-[#f26522]"
                  : "bg-white text-[#333333] hover:bg-[#fff5ef] border border-[#eeeeee]"
              }`}
              title="Justify"
              aria-label="Justify"
            >
              <FaAlignJustify size={16} />
            </button>
          </div>
        </div>
      </div>
      <form onSubmit={handleUpdate} className="space-y-4 min-w-0 max-w-full box-border">
        {/* Collaborators */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            <span className="font-semibold">Collaborators:</span>{" "}
            <span className="inline-flex flex-wrap gap-2">
              {(collabLists.collaborators || []).map((u) => (
                <span key={u._id} className="inline-flex items-center gap-1">
                  <span>@{u.username}</span>
                </span>
              ))}
              {(collabLists.collaborators || []).length === 0 && <span className="text-gray-400">None</span>}
            </span>
          </div>
          <button
            type="button"
            className="text-sm text-[#f26522] hover:underline"
            onClick={() => setCollabModal(true)}
          >
            Manage Collaboration
          </button>
        </div>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={`w-full min-w-0 max-w-full box-border border p-2 rounded break-words font-serif ${
            alignment === "center"
              ? "text-center"
              : alignment === "right"
              ? "text-right"
              : alignment === "justify"
              ? "text-justify"
              : "text-left"
          } ${title && title.length > 200 ? "border-orange-500" : ""}`}
          placeholder="Poem title"
          required
        />
        {title && title.length > 200 && (
          <p className="text-orange-600 text-xs mt-1">
            Title is {title.length} characters. Maximum allowed is 200 characters. ({title.length - 200} over limit)
          </p>
        )}
        <div>
          <label className="block font-medium mb-1">Picture (optional)</label>
          <div className="flex flex-wrap items-start gap-4">
            {coverImagePreview ? (
              <div className="relative">
                <img
                  src={coverImagePreview}
                  alt="Poem preview"
                  className="w-full max-w-xs h-40 object-cover rounded border border-[#eeeeee]"
                />
                <button
                  type="button"
                  onClick={handleRemoveCoverImage}
                  className="absolute top-2 right-2 bg-white/90 border border-[#eeeeee] rounded-full p-1 text-[#f26522] hover:bg-white"
                  aria-label="Remove image"
                >
                  <FaTimes size={14} />
                </button>
              </div>
            ) : poem?.coverImage && !removeCoverImage ? (
              <div className="relative">
                <img
                  src={getPoemImageUrl(poem.coverImage)}
                  alt="Poem visual"
                  className="w-full max-w-xs h-40 object-cover rounded border border-[#eeeeee]"
                />
                <button
                  type="button"
                  onClick={handleRemoveCoverImage}
                  className="absolute top-2 right-2 bg-white/90 border border-[#eeeeee] rounded-full p-1 text-[#f26522] hover:bg-white"
                  aria-label="Remove image"
                >
                  <FaTimes size={14} />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#eeeeee] rounded-md bg-white text-[#f26522] hover:bg-[#fff5ef] cursor-pointer">
                <FaImage />
                <span>Add a picture</span>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleCoverImageChange}
                  className="hidden"
                />
              </label>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            JPEG, PNG, GIF, or WebP. You can crop before uploading. Max 5MB.
          </p>
        </div>
        <div>
          <div className="mb-2 flex gap-2">
            <button
              type="button"
              onClick={() => onFormatClick("BOLD")}
              className={`px-2 py-1 border rounded font-bold ${
                getInlineStyleState("BOLD") === "active"
                  ? "bg-[#f26522] text-white"
                  : getInlineStyleState("BOLD") === "mixed"
                  ? "bg-yellow-300 text-black"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              aria-label="Bold"
            >
              B
            </button>
            <button
              type="button"
              onClick={() => onFormatClick("ITALIC")}
              className={`px-2 py-1 border rounded italic ${
                getInlineStyleState("ITALIC") === "active"
                  ? "bg-[#f26522] text-white"
                  : getInlineStyleState("ITALIC") === "mixed"
                  ? "bg-yellow-300 text-black"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              aria-label="Italic"
            >
              I
            </button>
            <button
              type="button"
              onClick={() => onFormatClick("UNDERLINE")}
              className={`px-2 py-1 border rounded underline ${
                getInlineStyleState("UNDERLINE") === "active"
                  ? "bg-[#f26522] text-white"
                  : getInlineStyleState("UNDERLINE") === "mixed"
                  ? "bg-yellow-300 text-black"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
              aria-label="Underline"
            >
              U
            </button>
            {/* alignment controls moved to header */}
          </div>
          <div
            className={`border rounded bg-white min-h-[200px] p-4 min-w-0 max-w-full box-border poem-align-${alignment} font-serif`}
          >
            <Editor
              key={alignment}
              editorState={editorState}
              onChange={setEditorState}
              handleKeyCommand={handleKeyCommand}
              textAlignment={alignment === "justify" ? "left" : alignment}
              placeholder="Poem content"
              blockStyleFn={() => `align-${alignment}`}
              onFocus={() => setEditorFocused(true)}
              onBlur={() => {
                setEditorFocused(false);
                // Collapse selection to end to clear style state
                const selection = editorState.getSelection();
                const content = editorState.getCurrentContent();
                const lastBlock = content.getLastBlock();
                const lastKey = lastBlock.getKey();
                const lastLength = lastBlock.getLength();
                setEditorState(
                  EditorState.forceSelection(
                    editorState,
                    SelectionState.createEmpty(lastKey).merge({
                      anchorOffset: lastLength,
                      focusOffset: lastLength,
                    })
                  )
                );
              }}
            />
          </div>
        </div>
        <div className="relative">
          <label className="block font-medium mb-1">Tags (optional)</label>
          <input
            type="text"
            value={tags}
            onChange={handleTagsChange}
            onFocus={() => setTagInputFocused(true)}
            onBlur={() => {
              // Delay hiding suggestions to allow clicks
              setTimeout(() => {
                setTagInputFocused(false);
                setShowTagSuggestions(false);
              }, 300);
            }}
            placeholder="e.g. love, poetry, nature"
            className="w-full min-w-0 max-w-full box-border p-2 border rounded"
          />
          {showTagSuggestions &&
            tagInputFocused &&
            tagSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
                {tagSuggestions.map((tagData, index) => (
                  <div
                    key={index}
                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm flex items-center justify-between"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleTagSuggestionClick(tagData.tag)}
                  >
                    <span className="text-gray-800">#{tagData.tag}</span>
                    <span className="text-gray-400 text-xs">
                      {tagData.count} poem{tagData.count !== 1 ? "s" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          <p className="text-gray-500 text-xs mt-1">
            Add hashtags to help others discover your poem. Separate multiple
            tags with commas. (Maximum 5 tags)
          </p>
        </div>
        <div className="rounded-xl border border-[#eeeeee] bg-white p-3">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!copyrightEnabled}
              onChange={(e) => setCopyrightEnabled(e.target.checked)}
            />
            <span className="min-w-0">
              <span className="block font-medium text-[#333333]">
                Add copyright notice
              </span>
              <span className="block text-xs text-[#777777] leading-relaxed">
                Displays as: © {new Date().getFullYear()}{" "}
                {(poem?.author?.username || "PeerPoetry").trim()}
              </span>
            </span>
          </label>
        </div>
        {isUploading && (
          <UploadProgressBar
            progress={uploadProgress}
            label={
              coverImageFile
                ? "Uploading poem and cover image..."
                : "Uploading poem..."
            }
          />
        )}
        <div className="flex flex-wrap gap-2 min-w-0">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={isUploading}
            className="flex-1 min-w-[8.5rem] bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? "Saving..." : "Save as Draft"}
          </button>
          {wasDraft ? (
            <button
              type="button"
              onClick={handlePublish}
              disabled={isUploading}
              className="flex-1 min-w-[8.5rem] bg-[#f26522] text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? "Uploading..." : "Publish"}
            </button>
          ) : (
            <button
              type="submit"
              disabled={isUploading}
              className="flex-1 min-w-[8.5rem] bg-[#f26522] text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? "Uploading..." : "Save Changes"}
            </button>
          )}
        </div>
      </form>
      <CoverImageCropModal
        open={isCropOpen}
        imageSrc={cropImageSrc}
        title="Crop poem cover image"
        onCancel={handleCoverCropCancel}
        onComplete={handleCoverCropComplete}
      />
      {/* Collaborate Modal */}
      {collabModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-10 sm:top-20 mx-auto p-5 border w-full max-w-sm shadow-lg rounded-md bg-white box-border">
            <div className="mt-1">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Invite Collaborator</h3>
              <p className="text-xs text-gray-500 mb-4">Maximum 1 collaborator per poem</p>
              <div className="poem-compose-row">
                <input
                  type="text"
                  value={inviteUsername}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInviteUsername(v);
                    if (collabDebounce) clearTimeout(collabDebounce);
                    setCollabDebounce(
                      setTimeout(async () => {
                        const q = v.trim();
                        if (!q) {
                          setCollabSuggestions([]);
                          setShowCollabSuggestions(false);
                          return;
                        }
                        try {
                          const res = await fetch(
                            getApiUrl(`/api/user/search?q=${encodeURIComponent(q)}`),
                            {
                              credentials: "include", // Include cookies for authentication
                            }
                          );
                          const users = (await res.json()) || [];
                          setCollabSuggestions(users.slice(0, 6));
                          setShowCollabSuggestions(true);
                        } catch {
                          setCollabSuggestions([]);
                          setShowCollabSuggestions(false);
                        }
                      }, 200)
                    );
                  }}
                  placeholder="Enter pen name"
                  className="flex-1 min-w-0 max-w-full box-border p-2 border rounded"
                />
                <button
                  type="button"
                  className="shrink-0 px-3 py-2 bg-[#f26522] text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={(collabLists.collaborators || []).length >= 1 || (collabLists.invites || []).some(i => i.status === "pending")}
                  onClick={async () => {
                    const u = inviteUsername.trim();
                    if (!u) return;
                    try {
                      const res = await fetch(getApiUrl(`/api/poems/${id}/collaborators/invite`), {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        credentials: "include",
                        body: JSON.stringify({ username: u })
                      });
                      const data = await res.json();
                      if (!res.ok) {
                        setModal({
                          open: true,
                          title: "Invite Failed",
                          message: data.error || "Failed to send invite",
                          type: "error",
                        });
                        return;
                      }
                      setInviteUsername("");
                      const r = await fetch(getApiUrl(`/api/poems/${id}/collaborators`));
                      if (r.ok) setCollabLists(await r.json());
                      setModal({
                        open: true,
                        title: "Success",
                        message: "Invite sent successfully!",
                        type: "success",
                      });
                    } catch (e) {
                      setModal({
                        open: true,
                        title: "Error",
                        message: e.message || "Failed to send invite",
                        type: "error",
                      });
                    }
                  }}
                >
                  Invite
                </button>
              </div>
              {showCollabSuggestions && collabSuggestions.length > 0 && (
                <ul className="mt-1 border rounded bg-white shadow max-h-40 overflow-y-auto">
                  {collabSuggestions.map((u) => (
                    <li
                      key={u._id}
                      className="px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer flex items-center gap-2"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={async () => {
                        try {
                          const res = await fetch(getApiUrl(`/api/poems/${id}/collaborators/invite`), {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            credentials: "include",
                            body: JSON.stringify({ username: u.username })
                          });
                          const data = await res.json();
                          if (!res.ok) {
                            setModal({
                              open: true,
                              title: "Invite Failed",
                              message: data.error || "Failed to send invite",
                              type: "error",
                            });
                            return;
                          }
                          setInviteUsername("");
                          setShowCollabSuggestions(false);
                          const r = await fetch(getApiUrl(`/api/poems/${id}/collaborators`));
                          if (r.ok) setCollabLists(await r.json());
                          setModal({
                            open: true,
                            title: "Success",
                            message: "Invite sent successfully!",
                            type: "success",
                          });
                        } catch (e) {
                          setModal({
                            open: true,
                            title: "Error",
                            message: e.message || "Failed to send invite",
                            type: "error",
                          });
                        }
                      }}
                    >
                      <img
                        src={getAvatarUrl(u.avatar)}
                        alt="avatar"
                        className="w-6 h-6 rounded-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src =
                            e.currentTarget.src === DEFAULT_AVATAR_DATA_URL
                              ? FALLBACK_AVATAR_SVG
                              : DEFAULT_AVATAR_DATA_URL;
                        }}
                      />
                      <span className="font-medium">@{u.username}</span>
                      {u.name && <span className="text-gray-400">({u.name})</span>}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-4">
                <div className="text-sm font-semibold text-gray-800 mb-1">Pending Invites</div>
                <ul className="text-sm text-gray-600 max-h-40 overflow-y-auto">
                  {(collabLists.invites || []).filter(i => i.status === "pending").map((i) => (
                    <li key={i.user?._id || i.user} className="py-1">@{i.user?.username || i.user}</li>
                  ))}
                  {((collabLists.invites || []).filter(i => i.status === "pending").length === 0) && (
                    <li className="text-gray-400">No pending invites</li>
                  )}
                </ul>
              </div>
              <div className="flex justify-end mt-4">
                <button
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                  onClick={() => setCollabModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
